import { Box, Combobox, InputBase, Stack, TextInput, useCombobox } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import { setSelectedTeams } from '../../../../store/process-analysis-store/process-analysis-store.actions';
import { baseBlack, baseWhite, inkLight, secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Checkbox } from '../../../../ui-library/checkbox/checkbox';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import classes from '../../../../ui-library/select/select.module.css';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { getTeamOptions } from './main-section.helpers';
import { usePlaceholderText } from './main-section.hooks';

const MAX_COMPARISON_TEAMS = 4;

export const ProjectComparisonSelector = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const teams = useGlobalStore((state) => state.teams);
  const globalSelectedTeamIds = useProcessAnalysisStore((state) => state.selectedTeams);
  const [localSelectedTeamIds, setLocalSelectedTeamIds] = useState<string[]>([]);

  const placeholderText = usePlaceholderText();
  const teamOptions = getTeamOptions(teams, searchTerm);

  // Initialize with first team if no selection
  useEffect(() => {
    if (teams.length > 0 && globalSelectedTeamIds.length === 0) {
      const defaultTeamId = teams[0]?.id;
      if (defaultTeamId) {
        setSelectedTeams([defaultTeamId]);
      }
    }
  }, [teams, globalSelectedTeamIds]);

  useEffect(() => {
    setLocalSelectedTeamIds(globalSelectedTeamIds);
  }, [globalSelectedTeamIds]);

  const { t } = useTranslation();
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearchTerm('');
    },
  });

  const handleTeamToggle = (teamId: string) => {
    setLocalSelectedTeamIds((current) => {
      if (current.includes(teamId)) {
        return current.filter((id) => id !== teamId);
      } else if (current.length < MAX_COMPARISON_TEAMS) {
        return [...current, teamId];
      }
      return current;
    });
  };

  const handleCompare = () => {
    setSelectedTeams(localSelectedTeamIds);
    combobox.closeDropdown();
  };

  const isCompareDisabled = localSelectedTeamIds.length === 0;

  const options = teamOptions.map((option) => {
    const isChecked = localSelectedTeamIds.includes(option.value);
    const disabled =
      !localSelectedTeamIds.includes(option.value) && localSelectedTeamIds.length >= MAX_COMPARISON_TEAMS;

    return (
      <Tooltip
        key={option.value}
        label={t('common.tooltips.max_comparison_limit', {
          count: MAX_COMPARISON_TEAMS,
        })}
        variant="white"
        disabled={!disabled}
        position="right"
      >
        <Combobox.Option value={option.value} key={option.value} p={0}>
          <Checkbox
            label={option.label}
            checked={isChecked}
            disabled={disabled}
            onChange={() => handleTeamToggle(option.value)}
            size="sm"
          />
        </Combobox.Option>
      </Tooltip>
    );
  });

  return (
    <Combobox store={combobox} position="bottom-start" offset={{ mainAxis: 4 }}>
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          radius="xl"
          onClick={() => combobox.toggleDropdown()}
          leftSection={<Icon name="folder" size={16} color={skyDark} />}
          leftSectionPointerEvents="none"
          rightSection={<Combobox.Chevron />}
          rightSectionPointerEvents="none"
          pointer
          classNames={classes}
          styles={{
            input: {
              fontSize: 12,
              fontWeight: 500,
              lineHeight: '12px',
              color: localSelectedTeamIds.length > 0 ? baseBlack : skyDark,
              cursor: 'pointer',
              textAlign: 'left',
              width: 300,
              height: 30,
              minHeight: 30,
              fontFamily: 'Figtree',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            },
          }}
        >
          {placeholderText}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown p={16} style={{ borderRadius: 8 }}>
        <Stack gap={16}>
          <TextInput
            placeholder={t('process_analysis.main_section.search_by_team_name')}
            radius="xl"
            size="sm"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            leftSection={<Icon name="search" variant="filled" size={16} color={inkLight} />}
            style={{ width: '100%' }}
            classNames={classes}
          />
          <Divider />
          {options.length > 0 ? (
            <Combobox.Options
              style={{
                maxHeight: 300,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                padding: 0,
              }}
            >
              {options}
            </Combobox.Options>
          ) : (
            <Box style={{ textAlign: 'center' }}>
              <Text size="small" color={skyDark}>
                {searchTerm
                  ? t('process_analysis.main_section.no_teams_found')
                  : t('process_analysis.main_section.no_teams_available')}
              </Text>
            </Box>
          )}
          <Divider />
          <Button
            variant="secondary"
            radius="xl"
            size="xs"
            leftSection={
              <Icon
                name={localSelectedTeamIds.length > 1 ? 'compare_arrows' : 'check'}
                color={isCompareDisabled ? secondaryBase : baseWhite}
                size={14}
                pointer
              />
            }
            fullWidth
            onClick={handleCompare}
            disabled={isCompareDisabled}
          >
            {localSelectedTeamIds.length > 1 ? t('common.actions.compare') : t('common.actions.apply')}
          </Button>
        </Stack>
      </Combobox.Dropdown>
    </Combobox>
  );
};
